import {
  App,
  Button,
  Flex,
  Form,
  Typography,
  Upload,
  type UploadFile,
  type UploadProps
} from 'antd'
import type { RcFile } from 'antd/es/upload'
import { ImagePlus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AvatarWithInitials } from 'src/components'
import { usePresignedUpload } from 'src/hooks/usePresignedUpload'

import { useStyles } from '../styles'

type Props = {
  name?: string
  fallbackName?: string
}

/**
 * Logo picker for the workspace-creation wizard. Reuses the same presigned
 * upload flow as `OrganizationAvatar`, but does not require an existing
 * organization — the selected file is buffered in form state and the actual
 * S3 PUT + webhook happen on wizard submit via `uploadFiles(...)`.
 */
export const LogoUploadField = ({ name = 'photo', fallbackName }: Props) => {
  const { t } = useTranslation()
  const { uploadFile } = usePresignedUpload()
  const { message } = App.useApp()
  const { styles } = useStyles()
  const [fileList, setFileList] = useState<UploadFile[]>([])

  const customRequest: UploadProps['customRequest'] = async ({
    file,
    onSuccess,
    onError
  }) => {
    try {
      const result = await uploadFile(file as RcFile, undefined, {
        isPublic: true
      })
      onSuccess?.(result, file)
    } catch (error) {
      onError?.(error as Error)
    }
  }

  const onChange: UploadProps['onChange'] = (info) => {
    setFileList(info.fileList)
    if (info?.fileList?.length > 0) {
      const current = info.fileList[0]
      if (current.originFileObj && !current.url && !current.preview) {
        current.preview = URL.createObjectURL(current.originFileObj)
      }
    }
  }

  const beforeUpload = (file: RcFile) => {
    const isImage = file.type.startsWith('image/')
    if (!isImage) {
      message.error(t('only_upload_image'))
    }
    return isImage || Upload.LIST_IGNORE
  }

  const current = fileList[0]
  const previewUrl = current?.url || current?.preview

  return (
    <Form.Item
      name={name}
      valuePropName="fileList"
      getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
      style={{ marginBottom: 0 }}
    >
      <Upload
        accept="image/*"
        customRequest={customRequest}
        maxCount={1}
        onChange={onChange}
        beforeUpload={beforeUpload}
        showUploadList={false}
      >
        <div className={styles.logoBlock}>
          {previewUrl ? (
            <img
              src={previewUrl}
              alt=""
              style={{
                width: 56,
                height: 56,
                borderRadius: 8,
                objectFit: 'cover',
                flexShrink: 0
              }}
            />
          ) : (
            <AvatarWithInitials
              name={fallbackName ?? ''}
              shape="square"
              size={56}
            />
          )}

          <Flex vertical gap={2} flex={1} style={{ minWidth: 0 }}>
            <Typography.Text className={styles.logoLabel}>
              {t('workspaces.create.logo.label')}
            </Typography.Text>
            <Typography.Text className={styles.logoHint}>
              {t('workspaces.create.logo.hint')}
            </Typography.Text>
          </Flex>

          <Flex gap={8}>
            <Button icon={<ImagePlus size={14} />} size="small">
              {current
                ? t('workspaces.create.logo.change')
                : t('workspaces.create.logo.upload')}
            </Button>
            {current ? (
              <Button
                size="small"
                icon={<Trash2 size={14} />}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  setFileList([])
                }}
                danger
              >
                {t('workspaces.create.logo.remove')}
              </Button>
            ) : null}
          </Flex>
        </div>
      </Upload>
    </Form.Item>
  )
}
